import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Storefront, Envelope, ArrowDown, ArrowUp, Warning, Calendar, Sparkle } from "@phosphor-icons/react";

const SAMPLE_PLACEHOLDER = `Please find below the yield variance summary for 14 May 2026 across all outlet.
📍 MAHIM
  • Roasted Lamb → +0.98 Kg 🟢 (Closing Stock was written incorrectly.)
📍 COLABA
  • Bacon → −0.10 Kg 🟢
  • Crab Meat → −0.03 Kg 🟢 (Kitchen was out of crab meat stock)
…
`;

export default function ClosingTab({ refreshKey }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [emailText, setEmailText] = useState("");
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/yield-variance", { params: { limit: 30 } });
      setReports(r.data || []);
    } catch {
      toast.error("Failed to load yield reports");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const importEmail = async () => {
    if (!emailText.trim()) {
      toast.error("Paste the email body first");
      return;
    }
    setImporting(true);
    try {
      const r = await api.post("/yield-variance/import", { text: emailText, source: "manual_paste" });
      toast.success(`Imported · ${r.data.outlet_count} outlets · ${r.data.total_items} items`);
      setShowImport(false);
      setEmailText("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div data-testid="closing-tab" className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-sm grid place-items-center bg-cyan-900/40 text-cyan-200">
              <Sparkle size={15} weight="duotone" />
            </div>
            <h2 className="font-heading font-bold text-2xl tracking-tight">Daily closing · yield variance</h2>
          </div>
          <div className="text-[11px] text-slate-400 mt-1.5">
            Imports Tushar's daily yield variance email. Paste the email body or wire up Gmail auto-fetch.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => setShowImport((v) => !v)}
            className="h-9 rounded-sm bg-cyan-600 hover:bg-cyan-500 text-cyan-50"
            data-testid="paste-email-btn"
          >
            <Envelope size={14} weight="bold" className="mr-1.5" />
            {showImport ? "Cancel" : "Paste email"}
          </Button>
        </div>
      </div>

      {showImport && (
        <div className="bg-white rounded-md border border-slate-200 p-4 shadow-sm" data-testid="email-paste-card">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400 mb-2">
            Paste the email body from Tushar's "Daily Closing Report"
          </div>
          <textarea
            value={emailText}
            onChange={(e) => setEmailText(e.target.value)}
            placeholder={SAMPLE_PLACEHOLDER}
            rows={14}
            className="w-full font-mono text-xs p-3 rounded-sm border border-slate-300 focus:outline-none"
            data-testid="email-textarea"
          />
          <div className="flex items-center gap-2 mt-3">
            <Button onClick={importEmail} disabled={importing} size="sm" className="h-9 bg-cyan-600 hover:bg-cyan-500 text-cyan-50 rounded-sm" data-testid="import-btn">
              {importing ? "Parsing…" : "Import"}
            </Button>
            <span className="text-[11px] text-slate-400">
              Recognises <code className="px-1 bg-cyan-950/40 rounded text-cyan-200">📍 OUTLET</code> /{" "}
              <code className="px-1 bg-cyan-950/40 rounded text-cyan-200">• Item → ±N Kg 🟢/🔴</code> format.
            </span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center text-sm text-slate-400 py-10">Loading…</div>
      ) : reports.length === 0 ? (
        <div className="text-center text-sm text-slate-300 py-12 border border-dashed border-slate-200 rounded-md bg-white">
          No yield reports yet. Click <strong>Paste email</strong> and drop Tushar's daily closing.
        </div>
      ) : (
        <div className="space-y-6">
          {reports.map((r) => (
            <ReportCard key={r.id} report={r} />
          ))}
        </div>
      )}
    </div>
  );
}

function ReportCard({ report }) {
  const totalShort = report.total_short_items || 0;
  const totalExcess = (report.total_items || 0) - totalShort;
  const niceDate = (() => {
    if (!report.report_date) return "—";
    const d = new Date(report.report_date);
    return isNaN(d.getTime()) ? report.report_date : d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  })();
  return (
    <div className="bg-white rounded-md border border-slate-200 shadow-sm overflow-hidden" data-testid={`report-${report.report_date}`}>
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-sm grid place-items-center bg-cyan-900/40 text-cyan-200">
            <Calendar size={16} weight="duotone" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">Yield variance summary</div>
            <div className="font-heading font-bold text-lg tracking-tight">{niceDate}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Pill icon={<ArrowDown size={11} weight="bold" />} label="SHORT" value={totalShort} cls="var-pill var-up" />
          <Pill icon={<ArrowUp size={11} weight="bold" />} label="EXCESS" value={totalExcess} cls="var-pill var-down" />
          <Pill icon={<Storefront size={11} weight="bold" />} label="OUTLETS" value={(report.outlets || []).length} cls="var-pill var-neutral" />
        </div>
      </div>

      <div className="divide-y divide-slate-100">
        {(report.outlets || []).map((o) => (
          <OutletBlock key={o.outlet} outlet={o} />
        ))}
      </div>
    </div>
  );
}

function OutletBlock({ outlet }) {
  return (
    <div className="px-5 py-4" data-testid={`outlet-${outlet.outlet}`}>
      <div className="flex items-center gap-2 mb-3">
        <Storefront size={15} weight="duotone" className="text-cyan-300" />
        <div className="font-heading font-bold text-base tracking-tight text-slate-100 uppercase">{outlet.outlet}</div>
        <div className="text-[10px] text-slate-400">· {outlet.items.length} item{outlet.items.length === 1 ? "" : "s"}</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {outlet.items.map((it, idx) => (
          <ItemRow key={`${outlet.outlet}-${idx}`} it={it} />
        ))}
      </div>
    </div>
  );
}

function ItemRow({ it }) {
  const isShort = it.status === "short";
  return (
    <div
      className={`flex items-start gap-3 px-3 py-2 rounded-sm border ${isShort ? "row-tint-up border-rose-500/30" : "row-tint-down border-emerald-500/25"}`}
      data-testid={`item-${it.item}`}
    >
      <span className={`var-pill ${isShort ? "var-up-strong" : "var-down-strong"} shrink-0`}>
        {isShort ? <Warning size={10} weight="fill" /> : <ArrowUp size={10} weight="bold" />}
        {isShort ? "SHORT" : "EXCESS"}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-semibold text-slate-100 text-sm">{it.item}</span>
          {it.category && <span className="text-[10px] text-slate-400 uppercase tracking-wider">· {it.category}</span>}
        </div>
        {it.reason && <div className="text-[11px] text-slate-400 mt-0.5 leading-snug">{it.reason}</div>}
      </div>
      <div className={`font-mono tabular-nums text-sm font-bold shrink-0 ${isShort ? "var-text-up" : "var-text-down"}`}>
        {it.amount_kg > 0 ? "+" : ""}{it.amount_kg.toFixed(2)} kg
      </div>
    </div>
  );
}

function Pill({ icon, label, value, cls }) {
  return (
    <span className={`${cls} text-[10px] uppercase tracking-wider`}>
      {icon}
      <span className="opacity-80">{label}</span>
      <span className="font-bold">{value}</span>
    </span>
  );
}
